import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calculator, MessageCircle } from "lucide-react";
import LeadModal from "@/components/site/LeadModal";
import PageSeo from "@/components/site/PageSeo";
import api from "@/lib/api";
import { useSettings, usePageCopy } from "@/contexts/SettingsContext";
import {
    resolveWhatsAppDigits,
    whatsappUrl,
    emiInquiryMessage,
} from "@/lib/contact";

const EmiPage = () => {
    const [params] = useSearchParams();
    const { pages = {}, settings } = useSettings() || {};
    const copy = usePageCopy("emi", { title: "EMI Calculator" });
    const downPct = (pages.emi_down_pct ?? 15) / 100;
    const downPctLabel = Math.round((pages.emi_down_pct ?? 15) * 10) / 10;
    const defaultRate = pages.emi_default_rate ?? 14;
    const tenureOptions = (pages.emi_tenures || "12,24,36,48,60").split(",").map((n) => parseInt(n.trim(), 10)).filter(Boolean);

    const urlPrice = parseInt(params.get("price") || "150000", 10) || 150000;
    const bikeId = params.get("bike") || "";

    const [price, setPrice] = useState(urlPrice);
    const [rate, setRate] = useState(defaultRate);
    const [tenure, setTenure] = useState(tenureOptions[1] || 24);
    const [leadOpen, setLeadOpen] = useState(false);
    const [bike, setBike] = useState(null);

    useEffect(() => {
        setPrice(urlPrice);
    }, [urlPrice]);

    useEffect(() => {
        setRate(defaultRate);
    }, [defaultRate]);

    useEffect(() => {
        if (!tenureOptions.includes(tenure) && tenureOptions.length) {
            setTenure(tenureOptions[1] || tenureOptions[0]);
        }
    }, [tenureOptions, tenure]);

    useEffect(() => {
        if (!bikeId) {
            setBike(null);
            return undefined;
        }
        const ctrl = new AbortController();
        api.get(`/bikes/${bikeId}`, { signal: ctrl.signal })
            .then((r) => setBike(r.data))
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                setBike(null);
            });
        return () => ctrl.abort();
    }, [bikeId]);

    const calc = useMemo(() => {
        const down = Math.round(price * downPct);
        const loan = price - down;
        const r = rate / 12 / 100;
        const n = tenure;
        const monthly = (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const total = monthly * n;
        const interestAmt = total - loan;
        let balance = loan;
        const yearly = [];
        let yearPrincipal = 0;
        let yearInterest = 0;
        for (let i = 1; i <= n; i++) {
            const interestI = balance * r;
            const principalI = monthly - interestI;
            balance -= principalI;
            yearPrincipal += principalI;
            yearInterest += interestI;
            if (i % 12 === 0 || i === n) {
                yearly.push({
                    year: Math.ceil(i / 12),
                    principal: Math.round(yearPrincipal),
                    interest: Math.round(yearInterest),
                    balance: Math.max(0, Math.round(balance)),
                });
                yearPrincipal = 0;
                yearInterest = 0;
            }
        }
        return {
            down, loan,
            monthly: Math.round(monthly),
            total: Math.round(total),
            interestAmt: Math.round(interestAmt),
            yearly,
        };
    }, [price, rate, tenure, downPct]);

    const fmt = (n) => `₹${n?.toLocaleString("en-IN")}`;
    const leadNote = emiInquiryMessage({ monthly: calc.monthly, tenure, price });
    const waHref = whatsappUrl(resolveWhatsAppDigits(settings), leadNote);

    const resultCard = (
        <div className="h-full flex flex-col rounded-2xl bg-[#0A2540] text-white p-5 sm:p-6">
            <p className="text-xs uppercase tracking-widest text-white/60">Monthly EMI</p>
            <p className="font-display font-bold text-4xl sm:text-5xl mt-2 break-words" data-testid="emi-monthly">{fmt(calc.monthly)}</p>
            <p className="text-sm text-white/70 mt-1">for {tenure} months</p>
            <div className="mt-5 space-y-2.5 text-sm border-t border-white/10 pt-4">
                <div className="flex justify-between gap-3"><span className="text-white/70">Total payable</span><strong data-testid="emi-total">{fmt(calc.total)}</strong></div>
                <div className="flex justify-between gap-3"><span className="text-white/70">Total interest</span><strong data-testid="emi-interest">{fmt(calc.interestAmt)}</strong></div>
                <div className="flex justify-between gap-3"><span className="text-white/70">Principal</span><strong>{fmt(calc.loan)}</strong></div>
            </div>
            <div className="mt-auto pt-5">
                <button
                    type="button"
                    onClick={() => setLeadOpen(true)}
                    className="btn btn-primary w-full justify-center"
                    data-testid="emi-apply"
                >
                    Enquire about EMI
                </button>
                <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 w-full min-h-[44px] flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ea952] text-white font-semibold text-sm"
                    data-testid="emi-whatsapp"
                >
                    <MessageCircle size={16} /> WhatsApp this EMI
                </a>
                <p className="text-[13px] text-white/60 mt-3 text-center">Sales will call with bank and in-house finance options.</p>
            </div>
        </div>
    );

    return (
        <div className="bg-[#F4F6F8]" data-testid="emi-page">
            <PageSeo pageKey="emi" />
            <div className="bg-[#0A2540] text-white">
                <div className="container-x py-8 md:py-10" data-testid="emi-header">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#E31837] flex items-center justify-center shrink-0">
                            <Calculator size={20} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-white/60">{copy.eyebrow || "Finance"}</p>
                    </div>
                    <h1 className="font-display font-bold text-[1.65rem] leading-[1.15] sm:text-3xl mt-3 max-w-3xl" style={{ textWrap: "balance" }}>
                        {copy.title}
                    </h1>
                    <p className="text-sm leading-relaxed text-white/75 mt-2 max-w-2xl" style={{ textWrap: "pretty" }}>
                        {copy.subtitle || "See the monthly payment before you visit a Servall outlet."}
                    </p>
                    {bike && (
                        <p className="text-sm text-white mt-2">
                            For{" "}
                            <Link to={`/bikes/${bike.id}`} className="font-semibold underline underline-offset-2">{bike.name}</Link>
                        </p>
                    )}
                </div>
            </div>

            <section className="container-x py-8 md:py-10">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 md:items-stretch">
                    <div className="md:col-span-3 rounded-2xl border border-[#e8eaee] bg-white p-4 sm:p-6">
                        <h2 className="font-display font-bold text-lg text-[#0A2540]">Loan details</h2>

                        <div className="mt-4">
                            <label className="block text-[13px] font-semibold text-[#0A2540] mb-1.5" htmlFor="emi-price">Bike price</label>
                            <input
                                id="emi-price"
                                type="number"
                                inputMode="numeric"
                                min="0"
                                value={price}
                                onChange={(e) => setPrice(parseInt(e.target.value, 10) || 0)}
                                className="input"
                                data-testid="emi-price"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="rounded-xl bg-[#F4F6F8] p-3 sm:p-4">
                                <p className="text-[13px] text-[#7a8597]">Down payment ({downPctLabel}%)</p>
                                <p className="font-display font-bold text-lg sm:text-xl text-[#0A2540] mt-1" data-testid="emi-down">{fmt(calc.down)}</p>
                            </div>
                            <div className="rounded-xl bg-[#F4F6F8] p-3 sm:p-4">
                                <p className="text-[13px] text-[#7a8597]">Loan amount</p>
                                <p className="font-display font-bold text-lg sm:text-xl text-[#0A2540] mt-1" data-testid="emi-loan">{fmt(calc.loan)}</p>
                            </div>
                        </div>

                        <div className="mt-5">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <label className="text-[13px] font-semibold text-[#0A2540]" htmlFor="emi-rate">Interest rate per year</label>
                                <span className="font-display font-bold text-[#E31837]" data-testid="emi-rate-value">{rate}%</span>
                            </div>
                            <input
                                id="emi-rate"
                                type="range"
                                min="8"
                                max="24"
                                step="0.5"
                                value={rate}
                                onChange={(e) => setRate(parseFloat(e.target.value))}
                                className="w-full"
                                data-testid="emi-rate"
                            />
                            <div className="flex justify-between text-[13px] text-[#7a8597] mt-1">
                                <span>8%</span>
                                <span>24%</span>
                            </div>
                        </div>

                        <div className="mt-5">
                            <p className="text-[13px] font-semibold text-[#0A2540] mb-2">Loan tenure</p>
                            <div className="flex flex-wrap gap-2" data-testid="emi-tenure">
                                {tenureOptions.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setTenure(t)}
                                        aria-pressed={tenure === t}
                                        className={`min-h-[44px] px-3.5 rounded-full text-sm font-medium border ${
                                            tenure === t
                                                ? "bg-[#E31837] border-[#E31837] text-white"
                                                : "bg-[#F4F6F8] border-transparent text-[#0A2540]"
                                        }`}
                                    >
                                        {t} months
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2 md:h-full">
                        {resultCard}
                    </div>
                </div>

                <div className="mt-6" data-testid="emi-table">
                    <h2 className="font-display font-bold text-lg text-[#0A2540] mb-3">Year by year</h2>
                    <div className="md:hidden space-y-2">
                        {calc.yearly.map((y) => (
                            <div key={y.year} className="rounded-2xl border border-[#e8eaee] bg-white p-4">
                                <p className="font-display font-bold text-[#0A2540]">Year {y.year}</p>
                                <dl className="mt-2 grid grid-cols-3 gap-2">
                                    <div>
                                        <dt className="text-[13px] text-[#7a8597]">Principal</dt>
                                        <dd className="text-[15px] font-medium text-[#0A2540] mt-0.5">{fmt(y.principal)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-[13px] text-[#7a8597]">Interest</dt>
                                        <dd className="text-[15px] font-medium text-[#E31837] mt-0.5">{fmt(y.interest)}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-[13px] text-[#7a8597]">Balance</dt>
                                        <dd className="text-[15px] font-medium text-[#0A2540] mt-0.5">{fmt(y.balance)}</dd>
                                    </div>
                                </dl>
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block rounded-2xl border border-[#e8eaee] bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[#7a8597] bg-[#F4F6F8]">
                                    <th className="py-3 px-4 font-semibold">Year</th>
                                    <th className="py-3 px-4 font-semibold">Principal</th>
                                    <th className="py-3 px-4 font-semibold">Interest</th>
                                    <th className="py-3 px-4 font-semibold">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {calc.yearly.map((y) => (
                                    <tr key={y.year} className="border-t border-[#eef0f3]">
                                        <td className="py-3 px-4 font-display font-bold text-[#0A2540]">Year {y.year}</td>
                                        <td className="py-3 px-4 text-[#0A2540]">{fmt(y.principal)}</td>
                                        <td className="py-3 px-4 text-[#E31837]">{fmt(y.interest)}</td>
                                        <td className="py-3 px-4 font-semibold text-[#0A2540]">{fmt(y.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <LeadModal
                open={leadOpen}
                onOpenChange={setLeadOpen}
                intent="loan_inquiry"
                bike={bike}
                note={leadNote}
            />
        </div>
    );
};

export default EmiPage;
